import type { ISearchHit } from "../../lib/types/search"

/** Characters before a query is worth searching. A single letter is noise. */
export const PALETTE_MIN_QUERY = 2

/** How many hits the palette shows. The rest live on the search page. */
export const PALETTE_HIT_LIMIT = 5

export interface IPaletteStarter {
  id: string
  label: string
}

export type TPaletteRow =
  | { kind: "ask"; id: "ask"; query: string }
  | { kind: "starter"; id: string; label: string }
  | { kind: "hit"; id: string; hit: ISearchHit }
  | { kind: "see-all"; id: "see-all"; query: string }

export interface IPaletteRowsInput {
  query: string
  /** False in Lite: search only, no Ask row and no starter questions. */
  aiEnabled: boolean
  hits: readonly ISearchHit[]
  starters: readonly IPaletteStarter[]
  /** True while the debounced search is still in flight — hide stale hits. */
  searching: boolean
}

/** One keyboard row plus its index in {@link buildPaletteRows}. */
export interface IIndexedPaletteRow {
  row: TPaletteRow
  index: number
}

export type TPaletteBlock =
  | { kind: "row"; item: IIndexedPaletteRow }
  | { kind: "group"; channel: string; items: IIndexedPaletteRow[] }

/**
 * A hit with no words and no meeting title is not a row. Leaving it out keeps
 * its channel from drawing an empty heading, and it does not use a slot in
 * the five-hit cap.
 */
function isVisibleHit(hit: ISearchHit): boolean {
  return hit.content.trim().length > 0 || hit.meetingSubject.trim().length > 0
}

function isHitRow(row: TPaletteRow): row is Extract<TPaletteRow, { kind: "hit" }> {
  return row.kind === "hit"
}

/**
 * The rows a palette shows, in keyboard order.
 *
 * Empty query + AI: starter questions. Any typed query + AI: "Ask AI about …"
 * first. Two or more characters, once search has settled: up to five visible
 * hits grouped by channel (first-seen order, which follows score), then
 * "See all". Blank hits are skipped before the cap.
 */
export function buildPaletteRows(input: IPaletteRowsInput): TPaletteRow[] {
  const trimmed = input.query.trim()
  if (trimmed.length === 0) {
    if (!input.aiEnabled) return []
    return input.starters.map((starter) => ({ kind: "starter", id: starter.id, label: starter.label }))
  }

  const rows: TPaletteRow[] = []
  if (input.aiEnabled) rows.push({ kind: "ask", id: "ask", query: trimmed })
  if (trimmed.length < PALETTE_MIN_QUERY || input.searching) return rows

  const visible: ISearchHit[] = []
  for (const hit of input.hits) {
    if (!isVisibleHit(hit)) continue
    visible.push(hit)
    if (visible.length >= PALETTE_HIT_LIMIT) break
  }

  const seen = new Map<string, ISearchHit[]>()
  const channels: string[] = []
  for (const hit of visible) {
    const list = seen.get(hit.sourceChannel)
    if (list) {
      list.push(hit)
      continue
    }
    seen.set(hit.sourceChannel, [hit])
    channels.push(hit.sourceChannel)
  }
  for (const channel of channels) {
    for (const hit of seen.get(channel) ?? []) {
      rows.push({ kind: "hit", id: hit.chunkId, hit })
    }
  }
  rows.push({ kind: "see-all", id: "see-all", query: trimmed })
  return rows
}

/**
 * Bunch consecutive hits that share a channel. A channel with no hits is not
 * a block, so the view never paints a heading for an empty group.
 */
export function groupPaletteBlocks(rows: readonly TPaletteRow[]): TPaletteBlock[] {
  const blocks: TPaletteBlock[] = []
  let group: { channel: string; items: IIndexedPaletteRow[] } | null = null

  const flush = (): void => {
    if (group && group.items.length > 0) {
      blocks.push({ kind: "group", channel: group.channel, items: group.items })
    }
    group = null
  }

  rows.forEach((row, index) => {
    if (!isHitRow(row)) {
      flush()
      blocks.push({ kind: "row", item: { row, index } })
      return
    }
    if (!group || group.channel !== row.hit.sourceChannel) {
      flush()
      group = { channel: row.hit.sourceChannel, items: [] }
    }
    group.items.push({ row, index })
  })
  flush()
  return blocks
}
